import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  GraduationCap, LogOut, CheckCircle, Lock, Trophy, Star, 
  Send, Loader2, Award, ChevronRight, Clock, Building, Cog, Phone,
  Sparkles, Medal, BookOpen, MessageSquare, ExternalLink, Key, Mail,
  Target, Zap, Shield, DollarSign, Users, AlertCircle, ArrowRight,
  CheckCircle2, XCircle, HelpCircle, Lightbulb, FileText, TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dynamic rendering
const IconMap = {
  Sparkles, Users, DollarSign, TrendingUp, Shield, FileText, Target,
  MessageSquare, Zap, Building, Award, AlertCircle, Phone
};

// Training Content - US BPO Lead Generation
const TRAINING_CONTENT = {
  1: {
    title: "US BPO Fundamentals & Our Services",
    icon: Building,
    color: "from-blue-500 to-cyan-500",
    badge: "Explorer",
    description: "Master US customer communication and our three core services: Personal Loans, Credit Score, and Identity Protection.",
    stages: [
      {
        id: "welcome",
        title: "Welcome to Creative Infotech",
        icon: "Sparkles",
        content: [
          { type: "intro", text: "Congratulations on joining Creative Infotech! You're about to embark on an exciting journey in the US BPO industry, helping American consumers with essential financial services." },
          { type: "highlight", title: "What You'll Be Doing", items: ["Speaking with US customers over phone calls", "Generating quality leads for financial services", "Helping people find solutions for loans, credit, and identity protection", "Building relationships and trust with customers"] },
          { type: "cards", items: [{ icon: "DollarSign", title: "Personal Loans", desc: "Helping customers consolidate debt or fund important needs" }, { icon: "TrendingUp", title: "Credit Score Services", desc: "Empowering people to monitor and improve their credit" }, { icon: "Shield", title: "Identity Protection", desc: "Safeguarding personal information in the digital age" }] }
        ]
      },
      {
        id: "us-culture",
        title: "Understanding US Culture & Communication",
        icon: "Users",
        content: [
          { type: "intro", text: "Success in US BPO starts with understanding American communication style. Americans appreciate directness, friendliness, and efficiency." },
          { type: "do-dont", title: "Communication Do's & Don'ts", dos: ["Use first names - 'Hi John' not 'Hello Mr. Smith'", "Be direct and get to the point quickly", "Show genuine interest with brief small talk", "Speak clearly at a moderate pace", "Smile while talking - it reflects in your voice!"], donts: ["Don't use heavy jargon or technical terms", "Don't speak too fast or too slow", "Don't interrupt - let them finish speaking", "Don't be overly formal or stiff", "Don't make assumptions about their situation"] },
          { type: "highlight", title: "US Time Zones - Know When to Call!", items: ["EST (Eastern): New York, Florida - 10:30 hrs behind IST", "CST (Central): Texas, Chicago - 11:30 hrs behind IST", "MST (Mountain): Arizona, Colorado - 12:30 hrs behind IST", "PST (Pacific): California, Washington - 13:30 hrs behind IST"] },
          { type: "tip", text: "Never call before 8 AM or after 9 PM customer's local time - this is a TCPA violation!" }
        ]
      },
      {
        id: "personal-loans",
        title: "Personal Loans - Complete Guide",
        icon: "DollarSign",
        content: [
          { type: "intro", text: "A personal loan is an unsecured loan that customers can use for virtually any purpose - from consolidating debt to funding home improvements or covering emergencies." },
          { type: "terms", title: "Key Terms You Must Know", items: [{ term: "APR", definition: "Annual Percentage Rate - the yearly cost including interest and fees" }, { term: "Principal", definition: "The original amount borrowed" }, { term: "Term", definition: "Loan duration, typically 2-7 years" }, { term: "Monthly Payment", definition: "Fixed amount paid each month" }, { term: "Origination Fee", definition: "One-time fee (usually 1-8% of loan)" }, { term: "Soft vs Hard Pull", definition: "Soft pull doesn't affect credit; hard pull may lower it" }] },
          { type: "highlight", title: "Why Customers Need Personal Loans", items: ["Debt Consolidation - Combine high-interest debts into one payment", "Medical Expenses - Cover unexpected healthcare costs", "Home Improvement - Fund repairs or renovations", "Major Purchases - Appliances, furniture, electronics", "Emergency Expenses - Car repairs or urgent needs"] },
          { type: "stats", title: "Quick Facts", items: [{ label: "Typical Loan Range", value: "$1,000 - $50,000" }, { label: "Average APR", value: "6% - 36%" }, { label: "Common Terms", value: "2-7 years" }, { label: "Min Credit Score", value: "580-640 (varies)" }] }
        ]
      },
      {
        id: "credit-score",
        title: "Credit Score Services - Complete Guide",
        icon: "TrendingUp",
        content: [
          { type: "intro", text: "A credit score is a 3-digit number (300-850) representing creditworthiness. It affects loan rates, apartment rentals, even job applications." },
          { type: "scale", title: "Credit Score Ranges", ranges: [{ range: "800-850", label: "Exceptional", color: "green", desc: "Best rates, instant approvals" }, { range: "740-799", label: "Very Good", color: "emerald", desc: "Great rates, easy approvals" }, { range: "670-739", label: "Good", color: "yellow", desc: "Decent rates, standard terms" }, { range: "580-669", label: "Fair", color: "orange", desc: "Higher rates, more scrutiny" }, { range: "300-579", label: "Poor", color: "red", desc: "Limited options, highest rates" }] },
          { type: "breakdown", title: "What Affects Credit Score?", items: [{ factor: "Payment History", percent: 35, desc: "On-time payments are crucial" }, { factor: "Credit Utilization", percent: 30, desc: "Keep balances below 30%" }, { factor: "Length of History", percent: 15, desc: "Older accounts are better" }, { factor: "Credit Mix", percent: 10, desc: "Variety of account types" }, { factor: "New Credit", percent: 10, desc: "Too many new accounts hurts" }] },
          { type: "highlight", title: "Our Services Help Customers", items: ["Monitor their score daily (not just annually)", "Get instant alerts for any changes", "Understand factors hurting their score", "Access tools and simulators to improve", "Dispute errors on their credit report"] }
        ]
      },
      {
        id: "identity-protection",
        title: "Identity Protection - Complete Guide",
        icon: "Shield",
        content: [
          { type: "intro", text: "Identity theft happens when someone steals personal information to commit fraud - opening accounts, making purchases, or filing taxes in someone's name." },
          { type: "alert", title: "Alarming Statistics", items: ["1 in 3 Americans have experienced identity theft", "$56 billion lost to identity fraud in 2020", "Every 2 seconds, someone's identity is stolen", "Average victim spends 200+ hours resolving issues"] },
          { type: "highlight", title: "What Our Protection Monitors", items: ["Social Security Number - Alerts if used anywhere", "Credit Cards & Bank Accounts - Unauthorized activity", "Dark Web Surveillance - If info appears for sale", "Public Records - Address changes, court records", "Medical ID Theft - Fraudulent healthcare claims"] },
          { type: "benefits", title: "Key Benefits", items: [{ benefit: "24/7 Monitoring", desc: "Round-the-clock surveillance" }, { benefit: "Real-time Alerts", desc: "Instant notifications" }, { benefit: "Dark Web Monitoring", desc: "Scans underground markets" }, { benefit: "$1M Insurance", desc: "Coverage for restoration costs" }, { benefit: "Restoration Support", desc: "Dedicated specialists" }] }
        ]
      },
      {
        id: "compliance",
        title: "Compliance & Legal Guidelines",
        icon: "FileText",
        content: [
          { type: "intro", text: "Following compliance rules protects you, the company, and our customers. Violations can result in heavy fines and legal action." },
          { type: "do-dont", title: "Critical Compliance Rules", dos: ["Always identify yourself and company clearly", "Respect Do-Not-Call requests immediately", "Follow approved script guidelines", "Document all customer interactions", "Transfer to supervisor if unsure"], donts: ["Never promise specific loan approval", "Never guarantee interest rates", "Never make false claims about services", "Never pressure customers aggressively", "Never share customer information"] },
          { type: "warning", title: "TCPA Rules (Must Follow!)", items: ["Cannot call before 8 AM or after 9 PM customer's time", "Must honor opt-out requests immediately", "Cannot use pre-recorded messages without consent", "Must identify yourself at start of every call", "Violations = $500-$1,500 per call in fines"] },
          { type: "tip", text: "When in doubt, always transfer to your supervisor. It's better to ask than to violate compliance!" }
        ]
      }
    ],
    quiz: {
      title: "Day 1 Knowledge Check",
      passingScore: 80,
      questions: [
        { id: 1, question: "What time should you NEVER call a US customer?", options: ["Before 10 AM their time", "Before 8 AM or after 9 PM their time", "During lunch hours", "On weekends"], correct: 1, explanation: "TCPA rules prohibit calls before 8 AM or after 9 PM in the customer's local time zone." },
        { id: 2, question: "What percentage of credit score is determined by payment history?", options: ["25%", "30%", "35%", "40%"], correct: 2, explanation: "Payment history makes up 35% of a credit score - the largest single factor." },
        { id: 3, question: "What is the typical loan range for personal loans?", options: ["$500 - $5,000", "$1,000 - $50,000", "$10,000 - $100,000", "$5,000 - $25,000"], correct: 1, explanation: "Personal loans typically range from $1,000 to $50,000." },
        { id: 4, question: "How often does identity theft occur in the US?", options: ["Every 10 seconds", "Every 5 seconds", "Every 2 seconds", "Every minute"], correct: 2, explanation: "Someone's identity is stolen every 2 seconds in the US." },
        { id: 5, question: "What credit score range is considered 'Good'?", options: ["580-669", "670-739", "740-799", "800-850"], correct: 1, explanation: "670-739 is considered 'Good' and qualifies for decent rates." }
      ]
    }
  },
  2: {
    title: "Sales Techniques & Lead Generation",
    icon: Target,
    color: "from-purple-500 to-pink-500",
    badge: "Learner",
    description: "Master lead generation, rapport building, and handling objections like a pro.",
    unlocksAfter: "CRM Access",
    stages: [
      {
        id: "lead-basics",
        title: "What Makes a Quality Lead?",
        icon: "Target",
        content: [
          { type: "intro", text: "A lead is not just a name and number - it's a potential customer who has shown genuine interest and meets our criteria. Quality over quantity!" },
          { type: "checklist", title: "Lead Qualification Checklist", items: [{ check: "Verified phone number that works", required: true }, { check: "Valid email address", required: true }, { check: "Expressed specific need for service", required: true }, { check: "US resident (required)", required: true }, { check: "18+ years of age", required: true }, { check: "Ready to speak with specialist", required: false }] },
          { type: "process", title: "The Lead Generation Flow", steps: [{ step: 1, title: "Initial Contact", desc: "Build rapport and establish trust" }, { step: 2, title: "Needs Discovery", desc: "Understand their specific situation" }, { step: 3, title: "Service Presentation", desc: "Explain how we can help" }, { step: 4, title: "Qualification", desc: "Verify they meet our criteria" }, { step: 5, title: "Lead Transfer", desc: "Connect with specialist" }] }
        ]
      },
      {
        id: "opening-scripts",
        title: "Opening Scripts & Rapport Building",
        icon: "MessageSquare",
        content: [
          { type: "intro", text: "The first 15 seconds determine everything. Your opening must be confident, clear, and engaging." },
          { type: "script", title: "The Perfect Opening", lines: [{ speaker: "You", text: "Good [morning/afternoon], this is [Your Name] from Creative Infotech. Am I speaking with [Customer Name]?" }, { speaker: "Note", text: "[Wait for confirmation - NEVER rush]" }, { speaker: "You", text: "Great! [Name], how are you doing today?" }, { speaker: "Note", text: "[Brief small talk - 10-15 seconds MAX]" }, { speaker: "You", text: "The reason for my call today is..." }] },
          { type: "highlight", title: "Rapport Building Techniques", items: ["Use their name 2-3 times (not more!)", "Mirror their speaking pace", "Show genuine interest", "Find common ground quickly", "Be enthusiastic but not over-the-top"] },
          { type: "tip", text: "SMILE while talking! It changes your entire tone. Keep a mirror at your desk!" }
        ]
      },
      {
        id: "loan-pitch",
        title: "Personal Loan - Sales Approach",
        icon: "DollarSign",
        content: [
          { type: "intro", text: "Personal loan customers are often stressed about debt. Be the helpful guide, not a pushy salesperson." },
          { type: "questions", title: "Discovery Questions", items: ["What would you use the funds for?", "Have you looked into personal loans before?", "What monthly payment would work for your budget?", "How soon would you need access to the funds?", "Are you paying high interest on credit cards?"] },
          { type: "pitch", title: "Key Selling Points", points: [{ point: "Fixed Monthly Payments", script: "Unlike credit cards, you'll know exactly what you pay each month." }, { point: "Lower Interest", script: "Many save hundreds by consolidating high-interest debt." }, { point: "Fast Funding", script: "Qualified customers can receive funds in 24-48 hours." }, { point: "No Collateral", script: "It's unsecured - no need to put up your car or home." }, { point: "Soft Credit Check", script: "Checking your rate won't affect your credit score." }] }
        ]
      },
      {
        id: "credit-pitch",
        title: "Credit Score - Sales Approach",
        icon: "TrendingUp",
        content: [
          { type: "intro", text: "Credit score customers are often planning a major purchase. Position yourself as their credit coach." },
          { type: "questions", title: "Discovery Questions", items: ["Do you know your current credit score?", "When did you last check your credit report?", "Are you planning major purchases next year?", "Have you ever found errors on your report?", "How often do you think about your credit?"] },
          { type: "pitch", title: "Key Selling Points", points: [{ point: "Daily Monitoring", script: "Free reports only show once a year. A lot changes in 365 days." }, { point: "Score Simulator", script: "See how actions would affect your score before doing them." }, { point: "Alert System", script: "Get notified instantly if something changes." }, { point: "Mortgage Savings", script: "50-point improvement could save $50,000+ on a mortgage." }, { point: "Error Detection", script: "1 in 5 reports contain errors. We help find and dispute them." }] }
        ]
      },
      {
        id: "identity-pitch",
        title: "Identity Protection - Sales Approach",
        icon: "Shield",
        content: [
          { type: "intro", text: "Identity theft is emotional. Customers feel violated and scared. Lead with empathy and position protection as peace of mind." },
          { type: "questions", title: "Discovery Questions", items: ["Have you or anyone you know been a victim?", "Do you shop or bank online regularly?", "Have you received breach notifications recently?", "How do you currently protect your info?", "Do you have family to protect as well?"] },
          { type: "pitch", title: "Key Selling Points", points: [{ point: "24/7 Protection", script: "While you sleep, we're watching. Our systems never take a break." }, { point: "Dark Web Monitoring", script: "We scan places you can't access to see if your info is being sold." }, { point: "$1M Insurance", script: "If the worst happens, you're covered for restoration and lost wages." }, { point: "Family Plans", script: "One plan can protect your entire family." }, { point: "Peace of Mind", script: "Stop worrying about 'what if' - know you're protected." }] }
        ]
      },
      {
        id: "objections",
        title: "Handling Objections Like a Pro",
        icon: "Zap",
        content: [
          { type: "intro", text: "Objections aren't rejection - they're requests for more information. Master these responses!" },
          { type: "objections", items: [{ objection: "I'm not interested", response: "I understand. May I ask - what's your biggest concern about [service]?", tip: "Dig deeper once" }, { objection: "I don't have time", response: "I respect your time. This takes 2 minutes - now or should I call back?", tip: "Offer alternative time" }, { objection: "I need to think about it", response: "Absolutely. What specific questions can I answer to help you decide?", tip: "Find the real concern" }, { objection: "It's too expensive", response: "I hear you. What would [the problem] cost you if left unaddressed?", tip: "Reframe as investment" }, { objection: "Send me information", response: "Happy to! Let me confirm your email... Actually, it's faster if I walk you through key points - just 2 minutes.", tip: "Redirect to conversation" }, { objection: "I have something similar", response: "Great that you're protected! May I ask who you're with? We include [unique benefit] that others don't.", tip: "Find competitive advantage" }] }
        ]
      }
    ],
    quiz: {
      title: "Day 2 Knowledge Check",
      passingScore: 80,
      questions: [
        { id: 1, question: "What's the FIRST thing after confirming you're speaking with the right person?", options: ["Start your sales pitch", "Ask how they're doing - build rapport", "Ask for their credit score", "Transfer to specialist"], correct: 1, explanation: "Brief rapport (10-15 seconds) establishes trust before your pitch." },
        { id: 2, question: "When customer says 'I need to think about it', what do you do?", options: ["Say okay and hang up", "Keep pushing until they say yes", "Ask what questions you can answer to help them decide", "Offer discount immediately"], correct: 2, explanation: "This often means unanswered questions. Find what's holding them back." },
        { id: 3, question: "Which is NOT required for a qualified lead?", options: ["Verified phone number", "US resident", "Ready to speak with specialist immediately", "18+ years of age"], correct: 2, explanation: "Being ready immediately is helpful but not required." },
        { id: 4, question: "What can save customers $50,000+ on credit score services pitch?", options: ["Having any score", "50-point improvement on 30-year mortgage", "Checking weekly vs monthly", "Multiple credit cards"], correct: 1, explanation: "50-point improvement significantly lowers mortgage rates." },
        { id: 5, question: "How handle 'Just send me information'?", options: ["Hang up and email", "Refuse to send", "Confirm email, redirect to walking through key points", "Send info and never call back"], correct: 2, explanation: "Use as opportunity to keep them engaged." }
      ]
    }
  },
  3: {
    title: "CRM, Systems & Quality Standards",
    icon: Cog,
    color: "from-orange-500 to-red-500",
    badge: "Technician",
    description: "Master the tools - CRM navigation, dialer systems, and quality standards.",
    unlocksAfter: "Email Access",
    stages: [
      {
        id: "crm-overview",
        title: "CRM System Mastery",
        icon: "Building",
        content: [
          { type: "intro", text: "CRM is your command center. Every interaction, note, and follow-up lives here. Master it = master your job." },
          { type: "highlight", title: "What You'll Use CRM For", items: ["Viewing customer info before calls", "Logging every call with notes", "Tracking lead status", "Scheduling follow-up callbacks", "Reviewing your metrics"] },
          { type: "process", title: "CRM Workflow", steps: [{ step: 1, title: "Review Lead", desc: "Check info before dialing" }, { step: 2, title: "Make Call", desc: "System auto-dials" }, { step: 3, title: "Take Notes", desc: "Document DURING the call" }, { step: 4, title: "Set Disposition", desc: "Mark outcome immediately" }, { step: 5, title: "Schedule Follow-up", desc: "If needed, set callback" }] },
          { type: "tip", text: "Golden Rule: If it's not in the CRM, it didn't happen. Document everything!" }
        ]
      },
      {
        id: "dispositions",
        title: "Call Dispositions Guide",
        icon: "FileText",
        content: [
          { type: "intro", text: "Dispositions tell the system what happened. Using wrong ones messes up reporting." },
          { type: "dispositions", title: "Know Your Dispositions", items: [{ code: "CONNECTED", desc: "Spoke with decision maker", when: "Real conversation with right person" }, { code: "VOICEMAIL", desc: "Left a message", when: "Left professional voicemail" }, { code: "NO ANSWER", desc: "No pickup, no VM", when: "No answer, no voicemail" }, { code: "WRONG NUMBER", desc: "Not the right person", when: "Person says wrong number" }, { code: "DO NOT CALL", desc: "Requested removal", when: "Customer wants off list - MUST honor" }, { code: "CALLBACK", desc: "Scheduled follow-up", when: "Customer wants callback" }, { code: "LEAD TRANSFERRED", desc: "Qualified and sent", when: "Successfully transferred" }] },
          { type: "warning", title: "Critical: DNC Requests", items: ["When someone says 'Don't call me' - STOP immediately", "Be polite: 'I'll remove you from our list right away'", "Mark as DNC BEFORE ending call", "Never try to convince them otherwise", "Calling DNC = serious compliance violation"] }
        ]
      },
      {
        id: "notes",
        title: "Writing Effective Call Notes",
        icon: "MessageSquare",
        content: [
          { type: "intro", text: "Good notes help the next person understand what happened. Bad notes waste time." },
          { type: "do-dont", title: "Notes Do's & Don'ts", dos: ["Write notes DURING the call", "Include specific details from customer", "Note objections and how you handled them", "Record promised follow-up actions", "Use standard abbreviations (CB=callback, LM=left message)"], donts: ["Don't write 'spoke with customer' - too vague", "Don't use full sentences when abbreviations work", "Don't include irrelevant personal details", "Don't skip notes because you're busy", "Don't use unprofessional language"] },
          { type: "examples", title: "Good vs Bad Notes", items: [{ bad: "Called, no answer", good: "NA x3, LM w/ CB request for 2pm EST Tue" }, { bad: "Customer interested", good: "Int in PL for debt consol. $8k CC debt @ 22% APR. Wants <$200/mo. Transfer 3pm" }, { bad: "Will call back", good: "CB req'd - vacation until 4/20. Call after 2pm PST. Int ID protection for family" }] }
        ]
      },
      {
        id: "quality",
        title: "Quality Assurance Standards",
        icon: "Award",
        content: [
          { type: "intro", text: "Every call may be recorded and evaluated. Understanding QA helps you score well." },
          { type: "scorecard", title: "QA Scorecard", items: [{ category: "Opening", points: 10, criteria: "Professional greeting, identification" }, { category: "Verification", points: 10, criteria: "Confirmed correct person" }, { category: "Discovery", points: 20, criteria: "Asked relevant questions" }, { category: "Presentation", points: 20, criteria: "Clear explanation, used benefits" }, { category: "Objection Handling", points: 15, criteria: "Addressed concerns professionally" }, { category: "Closing", points: 15, criteria: "Clear next steps" }, { category: "Compliance", points: 10, criteria: "Followed all rules" }] },
          { type: "stats", title: "Performance Targets", items: [{ label: "Min QA Score", value: "80%" }, { label: "Leads per Hour", value: "3+" }, { label: "Complaint Rate", value: "<5%" }, { label: "Compliance Rate", value: "100%" }] },
          { type: "tip", text: "Say this at start of EVERY call: 'This call may be monitored or recorded for quality purposes'" }
        ]
      },
      {
        id: "security",
        title: "Data Security & Protection",
        icon: "Shield",
        content: [
          { type: "intro", text: "You handle sensitive info daily. One mistake = identity theft, lawsuits, termination. Take this seriously." },
          { type: "warning", title: "NEVER Collect or Store", items: ["Full Social Security Numbers (only last 4 if needed)", "Complete credit card numbers", "Bank account numbers (beyond last 4)", "Passwords of any kind", "Security question answers"] },
          { type: "do-dont", title: "Security Best Practices", dos: ["Lock computer when away (Win + L)", "Use unique, strong passwords", "Report suspicious activities immediately", "Follow clean desk policy", "Shred printed documents"], donts: ["Don't share login credentials", "Don't take photos of screens", "Don't discuss customers in public", "Don't access data you don't need", "Don't store customer info on personal devices"] },
          { type: "alert", title: "If You Suspect a Breach", items: ["Stop immediately", "Don't try to 'fix it' yourself", "Report to supervisor within 5 minutes", "Document what happened", "Do not discuss with coworkers"] }
        ]
      }
    ],
    quiz: {
      title: "Day 3 Knowledge Check",
      passingScore: 80,
      questions: [
        { id: 1, question: "When should you write call notes?", options: ["After you hang up", "End of shift", "DURING the call", "Only for important calls"], correct: 2, explanation: "Notes during the call capture accurate details." },
        { id: 2, question: "Customer says 'Don't call me again!' - what first?", options: ["Try convincing once more", "Stop immediately, be polite, mark DNC", "Transfer to supervisor", "Call back in a month"], correct: 1, explanation: "Honor DNC immediately. Be polite, mark it, end call." },
        { id: 3, question: "Minimum QA score required?", options: ["70%", "75%", "80%", "85%"], correct: 2, explanation: "Minimum QA score is 80%." },
        { id: 4, question: "Which should you NEVER collect?", options: ["Email address", "Full Social Security Number", "Phone number", "Date of birth"], correct: 1, explanation: "Never collect full SSN. Only last 4 if absolutely needed." },
        { id: 5, question: "What must you say at start of every call?", options: ["How are you?", "This call may be monitored or recorded", "Are you interested?", "Can I have your credit card?"], correct: 1, explanation: "Call recording disclosure is legally required." }
      ]
    }
  },
  4: {
    title: "Mock Scenarios & Certification",
    icon: Phone,
    color: "from-green-500 to-emerald-500",
    badge: "Champion",
    description: "Put knowledge to test with realistic scenarios. Pass all challenges to earn certification!",
    stages: [
      {
        id: "scenario-prep",
        title: "Preparing for Mock Calls",
        icon: "Target",
        content: [
          { type: "intro", text: "You've learned the theory - now apply it. These simulate real customer interactions." },
          { type: "checklist", title: "Before You Begin", items: [{ check: "Review all scripts from Day 2", required: true }, { check: "Have objection responses ready", required: true }, { check: "Practice opening out loud", required: true }, { check: "Prepare for different moods", required: true }, { check: "Remember compliance rules", required: true }] },
          { type: "highlight", title: "You'll Face 4 Scenarios", items: ["Scenario 1: Personal Loan - Debt consolidation", "Scenario 2: Credit Score - First-time homebuyer", "Scenario 3: Identity Protection - Breach victim", "Scenario 4: Difficult Customer - Frustrated and skeptical"] }
        ]
      },
      {
        id: "scenario-loan",
        title: "Scenario 1: Personal Loan Customer",
        icon: "DollarSign",
        content: [
          { type: "scenario", customer: { name: "Michael Thompson", age: 42, location: "Houston, Texas", situation: "$15,000 credit card debt across 4 cards, paying 22% APR", goal: "Consolidate into one lower payment", personality: "Practical, numbers-focused, slightly skeptical" } },
          { type: "task", title: "Your Mission", steps: ["Build rapport - he's a Houston Texans fan", "Understand his debt situation fully", "Calculate potential savings ($275/mo interest alone)", "Address his 'another loan' concern", "Qualify and transfer to specialist"] },
          { type: "objections", title: "Expect These", items: [{ objection: "What's the interest rate?", hint: "Don't quote specific rates - depends on credit/lender" }, { objection: "Will this hurt my credit?", hint: "Soft pull won't affect score" }, { objection: "I don't want more debt", hint: "Same debt, smarter terms - not new debt" }] }
        ]
      },
      {
        id: "scenario-credit",
        title: "Scenario 2: Credit Score Customer",
        icon: "TrendingUp",
        content: [
          { type: "scenario", customer: { name: "Sarah Martinez", age: 28, location: "Phoenix, Arizona", situation: "Planning first home purchase next year, doesn't know score", goal: "Prepare and improve score for mortgage", personality: "Eager, excited, lots of questions" } },
          { type: "task", title: "Your Mission", steps: ["Connect with homeownership excitement", "Educate on why score matters for mortgage", "Explain score ranges and where she might be", "Show how our tools help prepare", "Qualify and enroll"] },
          { type: "objections", title: "Expect These", items: [{ objection: "I can check free", hint: "Daily monitoring catches issues faster than annual" }, { objection: "I think my score is fine", hint: "Thinking vs knowing - be sure before applying" }, { objection: "Is this a scam?", hint: "Stay calm, explain company, offer verification" }] }
        ]
      },
      {
        id: "scenario-identity",
        title: "Scenario 3: Identity Protection Customer",
        icon: "Shield",
        content: [
          { type: "scenario", customer: { name: "Robert Chen", age: 55, location: "Seattle, Washington", situation: "Got data breach notification from bank last week", goal: "Wants protection going forward", personality: "Anxious, concerned, ready to act" } },
          { type: "task", title: "Your Mission", steps: ["Show empathy - he's genuinely worried", "Validate concerns (breach is serious)", "Explain comprehensive protection", "Highlight insurance for peace of mind", "Close the sale - he's motivated"] },
          { type: "objections", title: "Expect These", items: [{ objection: "My bank monitors", hint: "Banks only monitor THEIR accounts, not all your info" }, { objection: "I'm careful with my info", hint: "Breaches happen to companies, not your fault" }, { objection: "Sounds expensive", hint: "Compare to 200+ hours victims spend recovering" }] }
        ]
      },
      {
        id: "scenario-difficult",
        title: "Scenario 4: Difficult Customer",
        icon: "AlertCircle",
        content: [
          { type: "scenario", customer: { name: "Linda Williams", age: 67, location: "Miami, Florida", situation: "Frustrated - 'too many calls today', skeptical", goal: "Initially wants you to stop, but might engage if respected", personality: "Irritated, short patience, but fair if respected" } },
          { type: "task", title: "Your Mission", steps: ["Do NOT get defensive or argue", "Acknowledge frustration genuinely", "Apologize for inconvenience", "Offer to help OR respect DNC wish", "End professionally regardless"] },
          { type: "do-dont", title: "Critical Do's & Don'ts", dos: ["Stay calm no matter what", "Use 'I completely understand'", "Let her vent without interrupting", "Offer callback at better time", "Thank her regardless of outcome"], donts: ["Don't argue or get defensive", "Don't take it personally", "Don't interrupt to pitch", "Don't convince her if she wants DNC", "Don't make promises you can't keep"] },
          { type: "tip", text: "Sometimes the best outcome is a respectful goodbye. Professionalism matters even when no sale." }
        ]
      },
      {
        id: "certification",
        title: "Certification & Next Steps",
        icon: "Award",
        content: [
          { type: "congrats", text: "Congratulations on completing your training! You've learned everything to succeed in US BPO lead generation." },
          { type: "checklist", title: "Certification Requirements", items: [{ check: "Complete all 4 training days", required: true }, { check: "Pass all quizzes (80%+ each)", required: true }, { check: "Complete mock scenarios", required: true }, { check: "Shadow 2 live calls with mentor", required: true }, { check: "Complete HR paperwork", required: true }] },
          { type: "timeline", title: "Your First Week Live", days: [{ day: "Days 1-2", activity: "Supervised calls with mentor" }, { day: "Days 3-5", activity: "Independent with check-ins" }, { day: "Week 1 Target", activity: "5 quality leads/day" }, { day: "By Week 3", activity: "Full quota expectations" }] },
          { type: "resources", title: "Your Success Toolkit", items: ["Quick reference cards at desk", "Supervisor hotline for questions", "Team chat for peer support", "Daily metrics dashboard", "Weekly coaching sessions"] },
          { type: "final", text: "Remember: Every expert was once a beginner. Stay positive, stay compliant, success will follow. Welcome to the team!" }
        ]
      }
    ],
    quiz: {
      title: "Final Certification Quiz",
      passingScore: 80,
      questions: [
        { id: 1, question: "Michael objects 'I don't want more debt.' How respond?", options: ["Pressure him to accept", "Explain same debt with smarter terms, not new debt", "Tell him no other options", "End call immediately"], correct: 1, explanation: "Reframe: consolidation restructures existing debt, not adding new." },
        { id: 2, question: "Sarah says she can check free. Best response?", options: ["Tell her she's wrong", "Daily monitoring catches issues faster than annual reports", "Offer big discount", "Hang up and call someone else"], correct: 1, explanation: "Free annual reports = snapshot. Daily monitoring = ongoing protection." },
        { id: 3, question: "Robert's bank monitors. Why still need identity protection?", options: ["He doesn't need it", "Banks only monitor their accounts, not all personal info", "To get more credit cards", "Banks don't actually monitor"], correct: 1, explanation: "Comprehensive protection monitors SSN, dark web, public records - more than banks." },
        { id: 4, question: "Linda says 'Stop calling me!' First response?", options: ["Keep pushing pitch", "Argue your call is different", "Calmly acknowledge and respect her request", "Transfer to supervisor"], correct: 2, explanation: "Stay calm, show empathy, honor DNC. Professionalism matters." },
        { id: 5, question: "Min QA score and Week 1 lead target?", options: ["70%, 3 leads/day", "80%, 5 leads/day", "90%, 10 leads/day", "75%, 7 leads/day"], correct: 1, explanation: "80% QA minimum, 5 quality leads/day for Week 1." }
      ]
    }
  }
};

const JoinedPortalDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [activeStage, setActiveStage] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  const joineeToken = localStorage.getItem("joineeToken");

  const fetchDashboard = useCallback(async () => {
    if (!joineeToken) {
      navigate("/joined");
      return;
    }

    try {
      const response = await axios.get(`${API}/joined/dashboard`, {
        headers: { Authorization: `Bearer ${joineeToken}` }
      });
      setDashboardData(response.data);
      const currentDay = response.data.progress?.current_day || 1;
      setActiveDay(currentDay);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("joineeToken");
        localStorage.removeItem("joineeInfo");
        toast.error("Session expired. Please login again.");
        navigate("/joined");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [joineeToken, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem("joineeToken");
    localStorage.removeItem("joineeInfo");
    navigate("/joined");
    toast.success("Logged out successfully");
  };

  const handleQuizSubmit = () => {
    const quiz = TRAINING_CONTENT[activeDay].quiz;
    let correct = 0;
    quiz.questions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) correct++;
    });
    const score = Math.round((correct / quiz.questions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const handleCompleteDay = async () => {
    if (!feedback.trim()) {
      toast.error("Please share your feedback before completing");
      return;
    }

    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/joined/complete-day`, 
        { day: activeDay, feedback: feedback.trim() },
        { headers: { Authorization: `Bearer ${joineeToken}` } }
      );
      toast.success(`Day ${activeDay} completed! Great job!`);
      setFeedback("");
      setShowQuiz(false);
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizScore(null);
      setActiveStage(0);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete day");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Render content block
  const renderContentBlock = (block, index) => {
    switch (block.type) {
      case "intro":
        return (
          <p key={index} className="font-dm text-white/80 text-base leading-relaxed mb-4">
            {block.text}
          </p>
        );
      
      case "highlight":
        return (
          <div key={index} className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-5 mb-4">
            <h4 className="font-dm font-semibold text-white mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
              {block.title}
            </h4>
            <ul className="space-y-2">
              {block.items.map((item, i) => (
                <li key={i} className="font-dm text-white/70 text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        );

      case "cards":
        return (
          <div key={index} className="grid md:grid-cols-3 gap-4 mb-4">
            {block.items.map((card, i) => {
              const CardIcon = IconMap[card.icon] || DollarSign;
              return (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <CardIcon className="w-6 h-6 text-white" />
                  </div>
                  <h5 className="font-dm font-semibold text-white mb-1">{card.title}</h5>
                  <p className="font-dm text-white/60 text-xs">{card.desc}</p>
                </div>
              );
            })}
          </div>
        );

      case "do-dont":
        return (
          <div key={index} className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <h4 className="font-dm font-semibold text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" /> DO
              </h4>
              <ul className="space-y-2">
                {block.dos.map((item, i) => (
                  <li key={i} className="font-dm text-white/70 text-sm flex items-start gap-2">
                    <span className="text-green-400">✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <h4 className="font-dm font-semibold text-red-400 mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5" /> DON'T
              </h4>
              <ul className="space-y-2">
                {block.donts.map((item, i) => (
                  <li key={i} className="font-dm text-white/70 text-sm flex items-start gap-2">
                    <span className="text-red-400">✗</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case "tip":
        return (
          <div key={index} className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4 mb-4 flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="font-dm text-yellow-200 text-sm">{block.text}</p>
          </div>
        );

      case "warning":
        return (
          <div key={index} className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-red-400 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> {block.title}
            </h4>
            <ul className="space-y-1">
              {block.items.map((item, i) => (
                <li key={i} className="font-dm text-white/70 text-sm">• {item}</li>
              ))}
            </ul>
          </div>
        );

      case "alert":
        return (
          <div key={index} className="bg-orange-500/20 border border-orange-500/30 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-orange-400 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> {block.title}
            </h4>
            <ul className="space-y-1">
              {block.items.map((item, i) => (
                <li key={i} className="font-dm text-white/70 text-sm">• {item}</li>
              ))}
            </ul>
          </div>
        );

      case "terms":
        return (
          <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-3">
              {block.items.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="font-mono text-purple-400 font-semibold text-sm min-w-[100px]">{item.term}</span>
                  <span className="font-dm text-white/70 text-sm">{item.definition}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "stats":
        return (
          <div key={index} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {block.items.map((stat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                <p className="font-dm text-white font-bold text-lg">{stat.value}</p>
                <p className="font-dm text-white/50 text-xs">{stat.label}</p>
              </div>
            ))}
          </div>
        );

      case "scale":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-2">
              {block.ranges.map((r, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg bg-${r.color}-500/20 border border-${r.color}-500/30`}>
                  <span className="font-mono font-bold text-white w-20">{r.range}</span>
                  <span className={`font-dm font-semibold text-${r.color}-400 w-24`}>{r.label}</span>
                  <span className="font-dm text-white/60 text-sm">{r.desc}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "breakdown":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-3">
              {block.items.map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-1">
                    <span className="font-dm text-white text-sm">{item.factor}</span>
                    <span className="font-dm text-purple-400 font-semibold">{item.percent}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                      style={{ width: `${item.percent * 2.5}%` }}
                    />
                  </div>
                  <p className="font-dm text-white/50 text-xs mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "benefits":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="grid gap-3">
              {block.items.map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="font-dm font-semibold text-white text-sm">{item.benefit}</p>
                    <p className="font-dm text-white/60 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "process":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="relative">
              {block.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-4 mb-4 last:mb-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center font-dm font-bold text-white flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="pt-2">
                    <p className="font-dm font-semibold text-white">{step.title}</p>
                    <p className="font-dm text-white/60 text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "checklist":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-2">
              {block.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${item.required ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {item.required ? '!' : '○'}
                  </div>
                  <span className="font-dm text-white/80 text-sm">{item.check}</span>
                  {item.required && <span className="font-dm text-red-400 text-xs ml-auto">Required</span>}
                </div>
              ))}
            </div>
          </div>
        );

      case "script":
        return (
          <div key={index} className="bg-slate-800/50 border border-white/10 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-3">
              {block.lines.map((line, i) => (
                <div key={i} className={`p-3 rounded-lg ${line.speaker === 'Note' ? 'bg-yellow-500/10 border-l-2 border-yellow-500' : 'bg-purple-500/10 border-l-2 border-purple-500'}`}>
                  <span className={`font-dm font-semibold text-sm ${line.speaker === 'Note' ? 'text-yellow-400' : 'text-purple-400'}`}>{line.speaker}:</span>
                  <p className="font-dm text-white/80 text-sm mt-1">{line.text}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "questions":
        return (
          <div key={index} className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-blue-400 mb-3 flex items-center gap-2">
              <HelpCircle className="w-5 h-5" /> {block.title}
            </h4>
            <ol className="space-y-2 list-decimal list-inside">
              {block.items.map((q, i) => (
                <li key={i} className="font-dm text-white/80 text-sm">{q}</li>
              ))}
            </ol>
          </div>
        );

      case "pitch":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-3">
              {block.points.map((p, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="font-dm font-semibold text-purple-400 mb-2">{p.point}</p>
                  <p className="font-dm text-white/70 text-sm italic">"{p.script}"</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "objections":
        return (
          <div key={index} className="mb-4">
            {block.title && <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>}
            <div className="space-y-3">
              {block.items.map((obj, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="font-dm font-semibold text-red-400 mb-2">"{obj.objection}"</p>
                  {obj.response && <p className="font-dm text-white/80 text-sm mb-2">→ {obj.response}</p>}
                  {obj.hint && <p className="font-dm text-yellow-400 text-xs flex items-center gap-1"><Lightbulb className="w-3 h-3" /> {obj.hint}</p>}
                  {obj.tip && <p className="font-dm text-green-400 text-xs mt-1">Tip: {obj.tip}</p>}
                </div>
              ))}
            </div>
          </div>
        );

      case "dispositions":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-2">
              {block.items.map((d, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-3 mb-1">
                    <code className="font-mono text-purple-400 font-bold text-sm">{d.code}</code>
                    <span className="font-dm text-white/60 text-sm">- {d.desc}</span>
                  </div>
                  <p className="font-dm text-white/50 text-xs">Use when: {d.when}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "examples":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-3">
              {block.items.map((ex, i) => (
                <div key={i} className="grid md:grid-cols-2 gap-2">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="font-dm text-red-400 text-xs mb-1">Bad:</p>
                    <p className="font-dm text-white/70 text-sm">{ex.bad}</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="font-dm text-green-400 text-xs mb-1">Good:</p>
                    <p className="font-dm text-white/70 text-sm">{ex.good}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "scorecard":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/10">
                  <tr>
                    <th className="px-4 py-2 text-left font-dm text-white/80 text-sm">Category</th>
                    <th className="px-4 py-2 text-center font-dm text-white/80 text-sm">Points</th>
                    <th className="px-4 py-2 text-left font-dm text-white/80 text-sm">Criteria</th>
                  </tr>
                </thead>
                <tbody>
                  {block.items.map((item, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-4 py-2 font-dm text-white text-sm">{item.category}</td>
                      <td className="px-4 py-2 text-center font-dm text-purple-400 font-bold">{item.points}</td>
                      <td className="px-4 py-2 font-dm text-white/60 text-sm">{item.criteria}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "scenario":
        return (
          <div key={index} className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-5 mb-4">
            <h4 className="font-dm font-semibold text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" /> Customer Profile
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-dm text-white"><span className="text-white/50">Name:</span> {block.customer.name}</p>
                <p className="font-dm text-white"><span className="text-white/50">Age:</span> {block.customer.age}</p>
                <p className="font-dm text-white"><span className="text-white/50">Location:</span> {block.customer.location}</p>
              </div>
              <div>
                <p className="font-dm text-white/80 text-sm"><span className="text-white/50">Situation:</span> {block.customer.situation}</p>
                <p className="font-dm text-white/80 text-sm"><span className="text-white/50">Goal:</span> {block.customer.goal}</p>
                <p className="font-dm text-yellow-400 text-sm"><span className="text-white/50">Personality:</span> {block.customer.personality}</p>
              </div>
            </div>
          </div>
        );

      case "task":
        return (
          <div key={index} className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
            <h4 className="font-dm font-semibold text-blue-400 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" /> {block.title}
            </h4>
            <ol className="space-y-2">
              {block.steps.map((step, i) => (
                <li key={i} className="font-dm text-white/80 text-sm flex items-start gap-2">
                  <span className="w-5 h-5 bg-blue-500/30 rounded-full flex items-center justify-center text-xs text-blue-300 flex-shrink-0">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        );

      case "timeline":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="space-y-2">
              {block.days.map((d, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                  <span className="font-dm font-semibold text-purple-400 min-w-[100px]">{d.day}</span>
                  <span className="font-dm text-white/70 text-sm">{d.activity}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "resources":
        return (
          <div key={index} className="mb-4">
            <h4 className="font-dm font-semibold text-white mb-3">{block.title}</h4>
            <div className="grid md:grid-cols-2 gap-2">
              {block.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="font-dm text-white/70 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "congrats":
        return (
          <div key={index} className="text-center py-6 mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <p className="font-dm text-white/80 text-lg">{block.text}</p>
          </div>
        );

      case "final":
        return (
          <div key={index} className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-6 text-center">
            <p className="font-dm text-white text-lg">{block.text}</p>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          <p className="font-dm text-white/60">Loading your training...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="font-dm text-white/60">Failed to load dashboard</p>
          <button onClick={() => navigate("/joined")} className="mt-4 text-purple-400 hover:underline font-dm">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const { joinee, progress, credentials } = dashboardData;
  const completedDays = progress?.completed_days || [];
  const currentDay = progress?.current_day || 1;
  const isTrainingComplete = completedDays.length >= 4;
  const activeContent = TRAINING_CONTENT[activeDay];
  const isDayCompleted = completedDays.includes(activeDay);
  const canCompleteDay = activeDay === currentDay && !isDayCompleted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="joined-dashboard">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <nav className="relative z-50 bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-playfair font-bold text-white text-lg">Creative Infotech</span>
                <span className="font-dm text-xs text-purple-300 block">US BPO TRAINING</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-dm text-sm text-white">{joinee.name}</p>
                <p className="font-dm text-xs text-white/50">{joinee.position}</p>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg font-dm text-sm" data-testid="joined-logout-button">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Progress Header */}
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-playfair text-2xl text-white mb-1">
                {isTrainingComplete ? "Training Complete! 🎉" : `Day ${currentDay}: ${TRAINING_CONTENT[currentDay]?.title}`}
              </h1>
              <p className="font-dm text-white/60 text-sm">{completedDays.length} of 4 days completed</p>
            </div>
            {isTrainingComplete && (
              <button onClick={() => setShowCertificateModal(true)} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl font-dm font-medium text-white">
                <Award className="w-5 h-5" /> View Certificate
              </button>
            )}
          </div>

          {/* Day Progress */}
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((day) => {
              const isCompleted = completedDays.includes(day);
              const isCurrent = day === currentDay;
              const isLocked = day > currentDay;
              const DayIcon = TRAINING_CONTENT[day].icon;
              return (
                <button
                  key={day}
                  onClick={() => !isLocked && setActiveDay(day)}
                  disabled={isLocked}
                  className={`flex flex-col items-center flex-1 ${!isLocked ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 transition-all ${
                    isCompleted ? `bg-gradient-to-br ${TRAINING_CONTENT[day].color}` : isCurrent ? 'bg-white/20 border-2 border-purple-500' : 'bg-white/5 border border-white/10'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-6 h-6 text-white" /> : isLocked ? <Lock className="w-5 h-5 text-white/30" /> : <DayIcon className="w-6 h-6 text-white/70" />}
                  </div>
                  <span className={`font-dm text-xs ${isCompleted ? 'text-white' : isCurrent ? 'text-purple-400' : 'text-white/30'}`}>Day {day}</span>
                  <span className="font-dm text-[10px] text-white/30">{TRAINING_CONTENT[day].badge}</span>
                </button>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full transition-all" style={{ width: `${(completedDays.length / 4) * 100}%` }} />
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Stage Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 sticky top-24">
              <h3 className="font-dm font-semibold text-white mb-4">Topics</h3>
              <div className="space-y-2">
                {activeContent.stages.map((stage, index) => {
                  const StageIcon = IconMap[stage.icon] || BookOpen;
                  return (
                    <button
                      key={stage.id}
                      onClick={() => { setActiveStage(index); setShowQuiz(false); }}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${activeStage === index && !showQuiz ? 'bg-purple-500/30 border border-purple-500/50' : 'bg-white/5 hover:bg-white/10 border border-transparent'}`}
                    >
                      <StageIcon className={`w-4 h-4 ${activeStage === index && !showQuiz ? 'text-purple-400' : 'text-white/50'}`} />
                      <span className={`font-dm text-sm ${activeStage === index && !showQuiz ? 'text-white' : 'text-white/70'}`}>{stage.title}</span>
                    </button>
                  );
                })}
                {canCompleteDay && (
                  <button
                    onClick={() => setShowQuiz(true)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${showQuiz ? 'bg-green-500/30 border border-green-500/50' : 'bg-green-500/10 hover:bg-green-500/20 border border-green-500/30'}`}
                  >
                    <HelpCircle className={`w-4 h-4 ${showQuiz ? 'text-green-400' : 'text-green-400/70'}`} />
                    <span className={`font-dm text-sm ${showQuiz ? 'text-white' : 'text-green-400'}`}>Take Quiz</span>
                  </button>
                )}
              </div>

              {/* Credentials */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <h3 className="font-dm font-semibold text-white mb-3">Credentials</h3>
                <div className={`p-3 rounded-xl mb-2 ${completedDays.includes(2) ? 'bg-blue-500/20' : 'bg-white/5'}`}>
                  <p className={`font-dm text-sm ${completedDays.includes(2) ? 'text-white' : 'text-white/40'}`}>CRM Access</p>
                  <p className="font-dm text-xs text-white/40">{completedDays.includes(2) ? 'Unlocked' : 'Complete Day 2'}</p>
                  {completedDays.includes(2) && credentials?.crm && (
                    <div className="mt-2 text-xs">
                      <p className="text-white/70">User: {credentials.crm.username}</p>
                      <p className="text-white/70">Pass: {credentials.crm.password}</p>
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-xl ${completedDays.includes(3) ? 'bg-orange-500/20' : 'bg-white/5'}`}>
                  <p className={`font-dm text-sm ${completedDays.includes(3) ? 'text-white' : 'text-white/40'}`}>Email Access</p>
                  <p className="font-dm text-xs text-white/40">{completedDays.includes(3) ? 'Unlocked' : 'Complete Day 3'}</p>
                  {completedDays.includes(3) && credentials?.email && (
                    <div className="mt-2 text-xs">
                      <p className="text-white/70">{credentials.email.address}</p>
                      <p className="text-white/70">Pass: {credentials.email.password}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {!showQuiz ? (
                <motion.div
                  key={`stage-${activeStage}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden"
                >
                  {/* Stage Header */}
                  <div className={`bg-gradient-to-r ${activeContent.color} p-6`}>
                    <div className="flex items-center gap-4">
                      {(() => {
                        const StageIcon = IconMap[activeContent.stages[activeStage].icon] || BookOpen;
                        return (
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <StageIcon className="w-6 h-6 text-white" />
                          </div>
                        );
                      })()}
                      <div>
                        <p className="font-dm text-white/70 text-sm">Stage {activeStage + 1} of {activeContent.stages.length}</p>
                        <h2 className="font-playfair text-xl text-white">{activeContent.stages[activeStage].title}</h2>
                      </div>
                    </div>
                  </div>

                  {/* Stage Content */}
                  <div className="p-6">
                    {activeContent.stages[activeStage].content.map((block, index) => renderContentBlock(block, index))}

                    {/* Navigation */}
                    <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
                      <button
                        onClick={() => setActiveStage(Math.max(0, activeStage - 1))}
                        disabled={activeStage === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg font-dm text-sm text-white disabled:opacity-30"
                      >
                        <ChevronRight className="w-4 h-4 rotate-180" /> Previous
                      </button>
                      {activeStage < activeContent.stages.length - 1 ? (
                        <button
                          onClick={() => setActiveStage(activeStage + 1)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-500 rounded-lg font-dm text-sm text-white"
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </button>
                      ) : canCompleteDay ? (
                        <button
                          onClick={() => setShowQuiz(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 rounded-lg font-dm text-sm text-white"
                        >
                          Take Quiz <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="quiz"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6">
                    <h2 className="font-playfair text-xl text-white flex items-center gap-3">
                      <HelpCircle className="w-6 h-6" />
                      {activeContent.quiz.title}
                    </h2>
                    <p className="font-dm text-white/70 text-sm mt-1">Pass with {activeContent.quiz.passingScore}% or higher</p>
                  </div>

                  <div className="p-6">
                    {!quizSubmitted ? (
                      <>
                        <div className="space-y-6">
                          {activeContent.quiz.questions.map((q, qIndex) => (
                            <div key={q.id} className="bg-white/5 rounded-xl p-5">
                              <p className="font-dm font-semibold text-white mb-4">{qIndex + 1}. {q.question}</p>
                              <div className="space-y-2">
                                {q.options.map((opt, oIndex) => (
                                  <button
                                    key={oIndex}
                                    onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: oIndex })}
                                    className={`w-full text-left p-3 rounded-lg border transition-all ${quizAnswers[q.id] === oIndex ? 'bg-purple-500/30 border-purple-500' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                                  >
                                    <span className="font-dm text-sm text-white">{String.fromCharCode(65 + oIndex)}. {opt}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={handleQuizSubmit}
                          disabled={Object.keys(quizAnswers).length < activeContent.quiz.questions.length}
                          className="w-full mt-6 bg-green-500 hover:bg-green-600 text-white font-dm font-medium py-3 rounded-xl disabled:opacity-50"
                        >
                          Submit Quiz
                        </button>
                      </>
                    ) : (
                      <>
                        <div className={`text-center py-8 rounded-xl mb-6 ${quizScore >= activeContent.quiz.passingScore ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${quizScore >= activeContent.quiz.passingScore ? 'bg-green-500' : 'bg-red-500'}`}>
                            {quizScore >= activeContent.quiz.passingScore ? <CheckCircle className="w-10 h-10 text-white" /> : <XCircle className="w-10 h-10 text-white" />}
                          </div>
                          <p className="font-playfair text-3xl text-white mb-2">{quizScore}%</p>
                          <p className="font-dm text-white/70">{quizScore >= activeContent.quiz.passingScore ? 'Congratulations! You passed!' : 'Keep learning and try again!'}</p>
                        </div>

                        {/* Show answers */}
                        <div className="space-y-4 mb-6">
                          {activeContent.quiz.questions.map((q, qIndex) => (
                            <div key={q.id} className={`p-4 rounded-xl ${quizAnswers[q.id] === q.correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                              <p className="font-dm font-semibold text-white text-sm mb-2">{qIndex + 1}. {q.question}</p>
                              <p className="font-dm text-sm"><span className="text-white/50">Your answer:</span> <span className={quizAnswers[q.id] === q.correct ? 'text-green-400' : 'text-red-400'}>{q.options[quizAnswers[q.id]]}</span></p>
                              {quizAnswers[q.id] !== q.correct && <p className="font-dm text-sm"><span className="text-white/50">Correct:</span> <span className="text-green-400">{q.options[q.correct]}</span></p>}
                              <p className="font-dm text-xs text-white/50 mt-2">{q.explanation}</p>
                            </div>
                          ))}
                        </div>

                        {quizScore >= activeContent.quiz.passingScore ? (
                          <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-5">
                            <h4 className="font-dm font-semibold text-white mb-3">Share Your Feedback to Complete</h4>
                            <textarea
                              value={feedback}
                              onChange={(e) => setFeedback(e.target.value)}
                              placeholder="What did you learn today? Any suggestions?"
                              rows={3}
                              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl font-dm text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500 resize-none mb-4"
                            />
                            <button
                              onClick={handleCompleteDay}
                              disabled={submittingFeedback || !feedback.trim()}
                              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-dm font-medium py-3 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {submittingFeedback ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5" /> Complete Day {activeDay}</>}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setQuizSubmitted(false); setQuizAnswers({}); setQuizScore(null); }}
                            className="w-full bg-purple-500 text-white font-dm font-medium py-3 rounded-xl"
                          >
                            Try Again
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Certificate Modal */}
      <AnimatePresence>
        {showCertificateModal && isTrainingComplete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowCertificateModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 p-1 rounded-3xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="bg-slate-900 rounded-[22px] p-8 text-center">
                <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="font-playfair text-3xl text-white mb-2">Certificate of Completion</h2>
                <p className="font-dm text-white/60 mb-6">This is to certify that</p>
                <p className="font-playfair text-4xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-4">{joinee.name}</p>
                <p className="font-dm text-white/60 mb-6">has successfully completed the US BPO Lead Generation Training at Creative Infotech</p>
                <div className="flex justify-center gap-4 mb-6">
                  {[1, 2, 3, 4].map((day) => (
                    <div key={day} className={`w-12 h-12 rounded-xl bg-gradient-to-br ${TRAINING_CONTENT[day].color} flex items-center justify-center`}>
                      <Medal className="w-6 h-6 text-white" />
                    </div>
                  ))}
                </div>
                <p className="font-dm text-sm text-white/40">Completed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <button onClick={() => setShowCertificateModal(false)} className="mt-6 px-8 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-dm text-white">Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JoinedPortalDashboard;
